import { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";

/**
 * Custom hook that fetches and manages version history for a legal document.
 * Loads all versions of the same document type and allows switching between them.
 * @param {object|null} doc - Document being edited; fetches all versions of same type
 * @param {boolean} isEdit - Whether we are in edit mode
 * @returns {object} versions list, selected ID, setter, preview sections, loading state
 */
function useVersionHistory(doc, isEdit) {
    const [versions, setVersions] = useState([]);
    const [selectedId, setSelectedId] = useState(doc?.id || null);
    const [previewSections, setPreviewSections] = useState(doc?.content?.sections || []);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isEdit || !doc?.type) return;
        setLoading(true);
        axios.get("/admin/legal/all", { params: { type: doc.type } })
            .then(res => {
                const sorted = (res.data || []).sort((a, b) => parseFloat(b.version) - parseFloat(a.version));
                setVersions(sorted);
            })
            .catch(() => toast.error("Failed to load version history"))
            .finally(() => setLoading(false));
    }, [isEdit, doc?.type, doc?.id]);

    useEffect(() => {
        const found = versions.find(v => v.id === selectedId);
        if (found?.content?.sections) setPreviewSections(found.content.sections);
    }, [selectedId, versions]);

    return { versions, selectedId, setSelectedId, previewSections, loading };
}

export default useVersionHistory;
